/**
 * Document Management System - Document Context
 *
 * This context provides state management for documents across components.
 * It handles document fetching, selection, uploading, and analysis.
 *
 * Directory: src/contexts/DocumentContext.tsx
 */

import React, { createContext, useContext, useReducer, useCallback, ReactNode } from 'react';

// Define interfaces for document-related types
// These would typically be imported from a types file
export interface Document {
  id: string;
  title: string;
  fileType: string;
  fileSize: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  isAnalyzed: boolean;
  url: string;
  content?: string;
}

export interface DocumentAnalysisEntity {
  id: string;
  type: string;
  name: string;
  aliases?: string[];
  category: string;
  description?: string;
  confidence: number;
  occurrences: Array<{
    pageNumber: number;
    position: { x: number; y: number };
    context: string;
  }>;
}

export interface DocumentAnalysisClause {
  id: string;
  type: string;
  title: string;
  content: string;
  pageNumber: number;
  position: { x: number; y: number; width: number; height: number };
  category: string;
  importance: 'low' | 'medium' | 'high' | 'critical';
  status: 'standard' | 'non-standard' | 'custom';
  recommendation?: string;
  confidence: number;
}

export interface DocumentAnalysisRisk {
  id: string;
  type: string;
  title: string;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  likelihood: 'unlikely' | 'possible' | 'likely' | 'certain';
  impact: 'minimal' | 'moderate' | 'significant' | 'severe';
  relatedClauses: string[];
  recommendation: string;
  pageNumber: number;
  position: { x: number; y: number };
  confidence: number;
}

export interface DocumentAnalysisResult {
  summary: {
    title: string;
    documentType: string;
    date: string;
    author: string;
    parties: string[];
    pageCount: number;
    wordCount: number;
    language: string;
    jurisdiction: string;
    keywords: string[];
    abstract: string;
  };
  entities: DocumentAnalysisEntity[];
  clauses: DocumentAnalysisClause[];
  risks: DocumentAnalysisRisk[];
  comparison: any | null; // Type could be more specific based on actual comparison structure
}

// State interface
export interface DocumentContextState {
  documents: Document[];
  selectedDocument: Document | null;
  loading: boolean;
  error: string | null;
}

// Actions interface
export interface DocumentContextActions {
  fetchDocuments: () => Promise<void>;
  fetchDocument: (id: string) => Promise<void>;
  uploadDocument: (file: File, title?: string) => Promise<void>;
  deleteDocument: (id: string) => Promise<void>;
  selectDocument: (document: Document | null) => void;
  analyzeDocument: (id: string) => Promise<DocumentAnalysisResult>;
}

// Context type
export interface DocumentContextType {
  state: DocumentContextState;
  actions: DocumentContextActions;
}

// Initial state
const initialState: DocumentContextState = {
  documents: [],
  selectedDocument: null,
  loading: false,
  error: null,
};

// Action types
type ActionType =
  | { type: 'FETCH_DOCUMENTS_REQUEST' }
  | { type: 'FETCH_DOCUMENTS_SUCCESS'; payload: Document[] }
  | { type: 'FETCH_DOCUMENTS_FAILURE'; payload: string }
  | { type: 'FETCH_DOCUMENT_REQUEST' }
  | { type: 'FETCH_DOCUMENT_SUCCESS'; payload: Document }
  | { type: 'FETCH_DOCUMENT_FAILURE'; payload: string }
  | { type: 'UPLOAD_DOCUMENT_REQUEST' }
  | { type: 'UPLOAD_DOCUMENT_SUCCESS'; payload: Document }
  | { type: 'UPLOAD_DOCUMENT_FAILURE'; payload: string }
  | { type: 'DELETE_DOCUMENT_REQUEST'; payload: string }
  | { type: 'DELETE_DOCUMENT_SUCCESS'; payload: string }
  | { type: 'DELETE_DOCUMENT_FAILURE'; payload: string }
  | { type: 'SELECT_DOCUMENT'; payload: Document | null }
  | { type: 'ANALYZE_DOCUMENT_REQUEST'; payload: string }
  | {
      type: 'ANALYZE_DOCUMENT_SUCCESS';
      payload: { documentId: string; result: DocumentAnalysisResult };
    }
  | { type: 'ANALYZE_DOCUMENT_FAILURE'; payload: string };

// Reducer
const documentReducer = (state: DocumentContextState, action: ActionType): DocumentContextState => {
  switch (action.type) {
    case 'FETCH_DOCUMENTS_REQUEST':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'FETCH_DOCUMENTS_SUCCESS':
      return {
        ...state,
        documents: action.payload,
        loading: false,
        error: null,
      };
    case 'FETCH_DOCUMENTS_FAILURE':
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case 'FETCH_DOCUMENT_REQUEST':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'FETCH_DOCUMENT_SUCCESS':
      return {
        ...state,
        selectedDocument: action.payload,
        loading: false,
        error: null,
      };
    case 'FETCH_DOCUMENT_FAILURE':
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case 'UPLOAD_DOCUMENT_REQUEST':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'UPLOAD_DOCUMENT_SUCCESS':
      return {
        ...state,
        documents: [...state.documents, action.payload],
        loading: false,
        error: null,
      };
    case 'UPLOAD_DOCUMENT_FAILURE':
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case 'DELETE_DOCUMENT_REQUEST':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'DELETE_DOCUMENT_SUCCESS':
      return {
        ...state,
        documents: state.documents.filter(doc => doc.id !== action.payload),
        selectedDocument:
          state.selectedDocument?.id === action.payload ? null : state.selectedDocument,
        loading: false,
        error: null,
      };
    case 'DELETE_DOCUMENT_FAILURE':
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case 'SELECT_DOCUMENT':
      return {
        ...state,
        selectedDocument: action.payload,
      };
    case 'ANALYZE_DOCUMENT_REQUEST':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'ANALYZE_DOCUMENT_SUCCESS':
      return {
        ...state,
        documents: state.documents.map(doc =>
          doc.id === action.payload.documentId ? { ...doc, isAnalyzed: true } : doc
        ),
        selectedDocument:
          state.selectedDocument?.id === action.payload.documentId
            ? { ...state.selectedDocument, isAnalyzed: true }
            : state.selectedDocument,
        loading: false,
        error: null,
      };
    case 'ANALYZE_DOCUMENT_FAILURE':
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    default:
      return state;
  }
};

// Create context
const DocumentContext = createContext<DocumentContextType | undefined>(undefined);

// Provider component
export const DocumentContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(documentReducer, initialState);

  // Fetch all documents
  const fetchDocuments = useCallback(async (): Promise<void> => {
    dispatch({ type: 'FETCH_DOCUMENTS_REQUEST' });

    try {
      // In a real implementation, this would call an API service
      // For now, we'll simulate a delay and return mock data
      await new Promise(resolve => setTimeout(resolve, 800));

      // Mock data for documents
      const mockDocuments: Document[] = Array.from({ length: 10 }, (_, index) => ({
        id: `doc${index + 1}`,
        title: `Document ${index + 1}`,
        fileType:
          index % 3 === 0 ? 'application/pdf' : index % 3 === 1 ? 'application/docx' : 'text/plain',
        fileSize: Math.floor(Math.random() * 5000000) + 500000,
        createdAt: new Date(Date.now() - Math.floor(Math.random() * 30) * 86400000).toISOString(),
        updatedAt: new Date(Date.now() - Math.floor(Math.random() * 15) * 86400000).toISOString(),
        createdBy: index % 2 === 0 ? 'John Doe' : 'Jane Smith',
        isAnalyzed: index % 3 === 0,
        url: `/documents/doc${index + 1}.${index % 3 === 0 ? 'pdf' : index % 3 === 1 ? 'docx' : 'txt'}`,
      }));

      dispatch({ type: 'FETCH_DOCUMENTS_SUCCESS', payload: mockDocuments });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch documents';
      dispatch({ type: 'FETCH_DOCUMENTS_FAILURE', payload: errorMessage });
      console.error('Error fetching documents:', err);
    }
  }, []);

  // Fetch a single document
  const fetchDocument = useCallback(
    async (id: string): Promise<void> => {
      dispatch({ type: 'FETCH_DOCUMENT_REQUEST' });

      try {
        // Check if document is already in state
        const existingDocument = state.documents.find(doc => doc.id === id);

        if (existingDocument) {
          dispatch({
            type: 'FETCH_DOCUMENT_SUCCESS',
            payload: existingDocument,
          });
          return;
        }

        // In a real implementation, this would call an API service
        // For now, we'll simulate a delay and return mock data
        await new Promise(resolve => setTimeout(resolve, 800));

        // Mock document data
        const mockDocument: Document = {
          id,
          title: 'Services Agreement',
          fileType: 'application/pdf',
          fileSize: 1250000,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          createdBy: 'John Doe',
          isAnalyzed: false,
          url: `/documents/${id}.pdf`,
          content: `
          SERVICES AGREEMENT
          
          This Services Agreement (the "Agreement") is entered into as of April 15, 2025 (the "Effective Date"), by and between ABC Corporation, a Delaware corporation with offices at 123 Main Street, Anytown, USA ("Client"), and XYZ Consulting, LLC, a California limited liability company with offices at 456 Oak Avenue, Othertown, USA ("Service Provider").
          
          1. SERVICES
          
          1.1 Service Provider shall perform the services described in Exhibit A attached hereto (the "Services") in accordance with the terms and conditions of this Agreement.
          
          1.2 Service Provider shall perform the Services in a professional and workmanlike manner, consistent with industry standards, and shall devote adequate resources to meet its obligations under this Agreement.
          
          2. COMPENSATION
          
          2.1 Client shall pay Service Provider the fees set forth in Exhibit B attached hereto.
          
          2.2 Service Provider shall invoice Client monthly for Services performed. Client shall pay all undisputed amounts within thirty (30) days of receipt of invoice.
          
          3. TERM AND TERMINATION
          
          3.1 This Agreement shall commence on the Effective Date and shall continue for a period of one (1) year, unless earlier terminated as provided herein.
          
          3.2 Either party may terminate this Agreement upon thirty (30) days' written notice to the other party.
          
          4. CONFIDENTIALITY
          
          4.1 Each party acknowledges that it may receive confidential information of the other party during the performance of this Agreement. Each party agrees to maintain the confidentiality of such information and not to disclose it to any third party without the prior written consent of the disclosing party.
          
          5. INTELLECTUAL PROPERTY
          
          5.1 All intellectual property created by Service Provider in the course of providing the Services shall be owned by Client, provided that Service Provider shall retain ownership of any pre-existing intellectual property used in connection with the Services.
          
          6. LIMITATION OF LIABILITY
          
          6.1 NEITHER PARTY SHALL BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO THIS AGREEMENT.
          
          6.2 IN NO EVENT SHALL EITHER PARTY'S AGGREGATE LIABILITY UNDER THIS AGREEMENT EXCEED THE TOTAL FEES PAID BY CLIENT TO SERVICE PROVIDER DURING THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM.
          
          7. MISCELLANEOUS
          
          7.1 This Agreement constitutes the entire agreement between the parties with respect to the subject matter hereof and supersedes all prior and contemporaneous agreements and understandings, whether oral or written.
          
          7.2 This Agreement shall be governed by the laws of the State of California, without regard to its conflict of laws principles.
          
          IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.
          
          ABC CORPORATION
          
          By: ____________________
          Name: John Smith
          Title: CEO
          
          XYZ CONSULTING, LLC
          
          By: ____________________
          Name: Jane Doe
          Title: Managing Partner
        `,
        };

        dispatch({ type: 'FETCH_DOCUMENT_SUCCESS', payload: mockDocument });
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : `Failed to fetch document with ID: ${id}`;
        dispatch({ type: 'FETCH_DOCUMENT_FAILURE', payload: errorMessage });
        console.error('Error fetching document:', err);
      }
    },
    [state.documents]
  );

  // Upload a document
  const uploadDocument = useCallback(async (file: File, title?: string): Promise<void> => {
    dispatch({ type: 'UPLOAD_DOCUMENT_REQUEST' });

    try {
      // In a real implementation, this would call an API service
      // For now, we'll simulate a delay and return mock data
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Generate a unique ID
      const id = `doc-${Date.now()}`;

      // Create document object
      const newDocument: Document = {
        id,
        title: title || file.name,
        fileType: file.type,
        fileSize: file.size,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'Current User',
        isAnalyzed: false,
        url: URL.createObjectURL(file),
      };

      dispatch({ type: 'UPLOAD_DOCUMENT_SUCCESS', payload: newDocument });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload document';
      dispatch({ type: 'UPLOAD_DOCUMENT_FAILURE', payload: errorMessage });
      console.error('Error uploading document:', err);
    }
  }, []);

  // Delete a document
  const deleteDocument = useCallback(async (id: string): Promise<void> => {
    dispatch({ type: 'DELETE_DOCUMENT_REQUEST', payload: id });

    try {
      // In a real implementation, this would call an API service
      // For now, we'll simulate a delay
      await new Promise(resolve => setTimeout(resolve, 800));

      dispatch({ type: 'DELETE_DOCUMENT_SUCCESS', payload: id });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : `Failed to delete document with ID: ${id}`;
      dispatch({ type: 'DELETE_DOCUMENT_FAILURE', payload: errorMessage });
      console.error('Error deleting document:', err);
    }
  }, []);

  // Select a document
  const selectDocument = useCallback((document: Document | null): void => {
    dispatch({ type: 'SELECT_DOCUMENT', payload: document });
  }, []);

  // Analyze a document
  const analyzeDocument = useCallback(async (id: string): Promise<DocumentAnalysisResult> => {
    dispatch({ type: 'ANALYZE_DOCUMENT_REQUEST', payload: id });

    try {
      // In a real implementation, this would call an API service
      // For now, we'll simulate a delay and return mock data
      await new Promise(resolve => setTimeout(resolve, 3000));

      // Mock analysis result
      const mockAnalysisResult: DocumentAnalysisResult = {
        summary: {
          title: 'Service Agreement',
          documentType: 'Contract',
          date: '2025-04-15',
          author: 'Legal Department',
          parties: ['ABC Corporation', 'XYZ Consulting, LLC'],
          pageCount: 5,
          wordCount: 1200,
          language: 'English',
          jurisdiction: 'California, USA',
          keywords: [
            'services',
            'agreement',
            'compensation',
            'confidentiality',
            'intellectual property',
          ],
          abstract:
            'This Service Agreement outlines the terms and conditions under which XYZ Consulting will provide services to ABC Corporation, including payment terms, confidentiality provisions, and intellectual property rights.',
        },
        entities: [
          {
            id: 'ent001',
            type: 'organization',
            name: 'ABC Corporation',
            aliases: ['ABC', 'Client'],
            category: 'Client',
            description: 'A Delaware corporation',
            confidence: 0.98,
            occurrences: [
              {
                pageNumber: 1,
                position: { x: 120, y: 150 },
                context: 'ABC Corporation, a Delaware corporation with offices at 123 Main Street',
              },
            ],
          },
          {
            id: 'ent002',
            type: 'organization',
            name: 'XYZ Consulting, LLC',
            aliases: ['XYZ', 'Service Provider'],
            category: 'Vendor',
            description: 'A California limited liability company',
            confidence: 0.97,
            occurrences: [
              {
                pageNumber: 1,
                position: { x: 250, y: 150 },
                context: 'XYZ Consulting, LLC, a California limited liability company',
              },
            ],
          },
          {
            id: 'ent003',
            type: 'person',
            name: 'John Smith',
            category: 'Representative',
            description: 'CEO of ABC Corporation',
            confidence: 0.95,
            occurrences: [
              {
                pageNumber: 5,
                position: { x: 300, y: 700 },
                context: 'Name: John Smith\nTitle: CEO',
              },
            ],
          },
          {
            id: 'ent004',
            type: 'person',
            name: 'Jane Doe',
            category: 'Representative',
            description: 'Managing Partner of XYZ Consulting',
            confidence: 0.95,
            occurrences: [
              {
                pageNumber: 5,
                position: { x: 300, y: 750 },
                context: 'Name: Jane Doe\nTitle: Managing Partner',
              },
            ],
          },
          {
            id: 'ent005',
            type: 'date',
            name: 'April 15, 2025',
            category: 'Effective Date',
            confidence: 0.99,
            occurrences: [
              {
                pageNumber: 1,
                position: { x: 150, y: 200 },
                context: 'as of April 15, 2025 (the "Effective Date")',
              },
            ],
          },
        ],
        clauses: [
          {
            id: 'cls001',
            type: 'services',
            title: 'Services',
            content:
              'Service Provider shall perform the services described in Exhibit A attached hereto (the "Services") in accordance with the terms and conditions of this Agreement.',
            pageNumber: 1,
            position: { x: 100, y: 300, width: 400, height: 50 },
            category: 'Core',
            importance: 'high',
            status: 'standard',
            confidence: 0.95,
          },
          {
            id: 'cls002',
            type: 'compensation',
            title: 'Compensation',
            content:
              'Client shall pay Service Provider the fees set forth in Exhibit B attached hereto. Service Provider shall invoice Client monthly for Services performed. Client shall pay all undisputed amounts within thirty (30) days of receipt of invoice.',
            pageNumber: 2,
            position: { x: 100, y: 100, width: 400, height: 70 },
            category: 'Financial',
            importance: 'critical',
            status: 'standard',
            confidence: 0.97,
          },
          {
            id: 'cls003',
            type: 'termination',
            title: 'Term and Termination',
            content:
              "This Agreement shall commence on the Effective Date and shall continue for a period of one (1) year, unless earlier terminated as provided herein. Either party may terminate this Agreement upon thirty (30) days' written notice to the other party.",
            pageNumber: 2,
            position: { x: 100, y: 200, width: 400, height: 70 },
            category: 'Contract Management',
            importance: 'high',
            status: 'standard',
            confidence: 0.96,
          },
          {
            id: 'cls004',
            type: 'confidentiality',
            title: 'Confidentiality',
            content:
              'Each party acknowledges that it may receive confidential information of the other party during the performance of this Agreement. Each party agrees to maintain the confidentiality of such information and not to disclose it to any third party without the prior written consent of the disclosing party.',
            pageNumber: 3,
            position: { x: 100, y: 100, width: 400, height: 80 },
            category: 'Legal Protection',
            importance: 'high',
            status: 'standard',
            confidence: 0.98,
          },
          {
            id: 'cls005',
            type: 'intellectual_property',
            title: 'Intellectual Property',
            content:
              'All intellectual property created by Service Provider in the course of providing the Services shall be owned by Client, provided that Service Provider shall retain ownership of any pre-existing intellectual property used in connection with the Services.',
            pageNumber: 3,
            position: { x: 100, y: 200, width: 400, height: 70 },
            category: 'Legal Protection',
            importance: 'critical',
            status: 'standard',
            confidence: 0.97,
          },
          {
            id: 'cls006',
            type: 'limitation_of_liability',
            title: 'Limitation of Liability',
            content:
              "NEITHER PARTY SHALL BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO THIS AGREEMENT. IN NO EVENT SHALL EITHER PARTY'S AGGREGATE LIABILITY UNDER THIS AGREEMENT EXCEED THE TOTAL FEES PAID BY CLIENT TO SERVICE PROVIDER DURING THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM.",
            pageNumber: 4,
            position: { x: 100, y: 100, width: 400, height: 100 },
            category: 'Risk Allocation',
            importance: 'critical',
            status: 'non-standard',
            recommendation:
              'Consider adding carve-outs for breaches of confidentiality and intellectual property provisions.',
            confidence: 0.94,
          },
          {
            id: 'cls007',
            type: 'governing_law',
            title: 'Governing Law',
            content:
              'This Agreement shall be governed by the laws of the State of California, without regard to its conflict of laws principles.',
            pageNumber: 4,
            position: { x: 100, y: 300, width: 400, height: 50 },
            category: 'Legal Framework',
            importance: 'medium',
            status: 'standard',
            confidence: 0.99,
          },
        ],
        risks: [
          {
            id: 'risk001',
            type: 'legal',
            title: 'Broad Limitation of Liability',
            description:
              'The limitation of liability clause does not contain carve-outs for breaches of confidentiality or intellectual property provisions, which is unusual and could expose the client to significant risk.',
            severity: 'high',
            likelihood: 'possible',
            impact: 'significant',
            relatedClauses: ['cls006'],
            recommendation:
              'Add carve-outs for breaches of confidentiality and intellectual property provisions to the limitation of liability clause.',
            pageNumber: 4,
            position: { x: 100, y: 100 },
            confidence: 0.92,
          },
          {
            id: 'risk002',
            type: 'operational',
            title: 'Missing Service Level Agreement',
            description:
              'The agreement does not contain specific service levels or performance metrics, which could make it difficult to measure the quality of services provided.',
            severity: 'medium',
            likelihood: 'likely',
            impact: 'moderate',
            relatedClauses: ['cls001'],
            recommendation:
              'Add a service level agreement (SLA) as an exhibit to the contract, defining specific performance metrics and remedies for failure to meet them.',
            pageNumber: 1,
            position: { x: 100, y: 300 },
            confidence: 0.89,
          },
          {
            id: 'risk003',
            type: 'financial',
            title: 'Unclear Payment Terms',
            description:
              "The agreement states that Client shall pay 'undisputed amounts' but does not define a process for disputing invoices or resolving payment disputes.",
            severity: 'medium',
            likelihood: 'possible',
            impact: 'moderate',
            relatedClauses: ['cls002'],
            recommendation:
              'Add language defining the process for disputing invoices and resolving payment disputes.',
            pageNumber: 2,
            position: { x: 100, y: 100 },
            confidence: 0.87,
          },
        ],
        comparison: null,
      };

      dispatch({
        type: 'ANALYZE_DOCUMENT_SUCCESS',
        payload: { documentId: id, result: mockAnalysisResult },
      });

      return mockAnalysisResult;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : `Failed to analyze document with ID: ${id}`;
      dispatch({ type: 'ANALYZE_DOCUMENT_FAILURE', payload: errorMessage });
      console.error('Error analyzing document:', err);
      throw err;
    }
  }, []);

  // Create actions object
  const actions: DocumentContextActions = {
    fetchDocuments,
    fetchDocument,
    uploadDocument,
    deleteDocument,
    selectDocument,
    analyzeDocument,
  };

  return <DocumentContext.Provider value={{ state, actions }}>{children}</DocumentContext.Provider>;
};

// Custom hook to use the document context
export const useDocumentContext = (): DocumentContextType => {
  const context = useContext(DocumentContext);

  if (context === undefined) {
    throw new Error('useDocumentContext must be used within a DocumentContextProvider');
  }

  return context;
};
